import React, { useState } from 'react'
import { ImageOff, Loader2, Eye, Merge } from 'lucide-react'
import { ReviewBrand } from '../../services/broadcastAdminService'
import { valueOf, formatMoney } from './broadcastValuation'
import {
  REASONS, secs, BrandReviewSource, MARK_LABELS, MARK_STYLES
} from './broadcastReviewConstants'

interface Props {
  brand: ReviewBrand
  customerId: string
  broadcastId: string
  disabled: boolean
  /**
   * The broadcast's 30-second rate, or null when it is not priced. Passed in
   * rather than read here: one broadcast, one rate, and recomputing it per
   * card is how 24 cards end up disagreeing by a cent.
   */
  rate?: number | null
  source: BrandReviewSource
  onOpen: () => void
  onExclude: (reason: string | null) => void
}

/**
 * One brand, with the frame that proves it.
 *
 * The thumbnail is the point. A reviewer looking at the word "descript" in a
 * table cannot tell whether it is a sponsor or a screen-recorder watermark; a
 * reviewer looking at the frame can tell in under a second. The first load of
 * each card makes the backend seek the video, so a spinner here is expected
 * once and never again.
 */
const BroadcastBrandCard: React.FC<Props> = ({
  brand, customerId, broadcastId, disabled, rate = null, source, onOpen, onExclude
}) => {
  const [imgState, setImgState] = useState<'loading' | 'ok' | 'error'>('loading')
  // `counts` is the server's verdict and already folds in is_own, exclusion
  // and the mark rule. Recomputing it here is exactly how the page and the
  // totals would come to disagree.
  const off = !brand.counts
  const canShowFrame = !!brand.peak_appearance_id

  /**
   * Sponsored cards have no footer at all now. The partner's name used to
   * sit there behind a checkmark, which was the only way to learn that
   * "derringer" is really Derringer — but the heading says that itself once
   * it shows display_name, and repeating it under a tick was two readings
   * of one fact.
   */
  const footer = brand.is_sponsored
    ? null
    : brand.is_own ? (
        <span className="text-[11px] text-gray-400">Excluded automatically</span>
      ) : brand.excluded_reason ? (
        <button onClick={() => onExclude(null)} disabled={disabled}
          className="inline-flex items-center text-xs text-blue-600 hover:text-blue-700 disabled:opacity-40">
          <Eye className="h-3.5 w-3.5 mr-1" />Restore
        </button>
      ) : (
        <select
          value=""
          disabled={disabled}
          onChange={(e) => e.target.value && onExclude(e.target.value)}
          className="w-full text-xs border border-gray-300 rounded px-1.5 py-1 bg-white text-gray-600 disabled:opacity-40"
        >
          <option value="">Exclude…</option>
          {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      )

  return (
    <div className={`bg-white rounded-lg border overflow-hidden flex flex-col transition-all ${
      off ? 'border-gray-200 opacity-60' : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
    }`}>
      <button
        type="button"
        onClick={onOpen}
        className="relative aspect-video bg-gray-100 block w-full group"
        title="Open all appearances"
      >
        {canShowFrame && imgState !== 'error' ? (
          <>
            <img
              ref={(el) => {
                // Cached frames can finish before onLoad is attached; without
                // this the spinner outlives the image it is waiting for.
                if (el?.complete && el.naturalWidth > 0) setImgState('ok')
              }}
              src={source.getFrameUrl(customerId, broadcastId, brand.peak_appearance_id!)}
              alt={`${brand.display_name ?? brand.brand_name} at peak visibility`}
              className="w-full h-full object-cover"
              crossOrigin="use-credentials"
              onLoad={() => setImgState('ok')}
              onError={() => setImgState('error')}
            />
            {imgState === 'loading' && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 gap-1">
            <ImageOff className="h-6 w-6" />
            <span className="text-[10px]">No frame</span>
          </div>
        )}

        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/90 text-gray-800 text-[11px] font-medium">
            <Eye className="h-3 w-3" />{brand.appearances} appearance{brand.appearances === 1 ? '' : 's'}
          </span>
        </div>

        {brand.peak_frame_ms != null && (
          <span className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/65 text-white text-[10px] tabular-nums">
            {secs(brand.peak_frame_ms)}
          </span>
        )}
      </button>

      <div className="p-3 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <span className="font-medium text-sm text-gray-900 truncate">{brand.display_name ?? brand.brand_name}</span>
          {/* Only the EXCEPTIONS are labelled. `sponsor` is the default reading
              of every row, so a chip on the majority would say nothing — and a
              promoted mark keeps its type visible while still counting. */}
          {/* Not an "excluded" pill: the brand still counts. This says some of
              its occurrences were banned, which is a different fact and the
              only honest one to show while the totals still include it. */}
          {/* Says the row is several names reported as one, which is otherwise
              invisible — the aliases have no row of their own. */}
          {brand.merged_keys?.length > 0 && (
            <span
              title={`Reported together with: ${brand.merged_keys.join(', ')}`}
              className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border text-[10px] font-medium bg-indigo-50 text-indigo-700 border-indigo-200"
            >
              <Merge className="h-2.5 w-2.5" />{brand.merged_keys.length + 1}
            </span>
          )}
          {brand.excluded_appearances > 0 && !brand.excluded_reason && (
            <span
              title={`${brand.excluded_appearances} occurrence${brand.excluded_appearances === 1 ? '' : 's'} excluded and not counted`}
              className="flex-shrink-0 px-1.5 py-0.5 rounded border text-[10px] font-medium bg-rose-50 text-rose-700 border-rose-200"
            >
              −{brand.excluded_appearances}
            </span>
          )}
          {brand.mark_type && brand.mark_type !== 'sponsor' && (
            <span
              title={brand.is_promoted
                ? `${MARK_LABELS[brand.mark_type]} — counts because it is on this customer's partner list`
                : `${MARK_LABELS[brand.mark_type]}${brand.mark_source === 'reviewer' ? ', set by a reviewer' : ''}`}
              className={`flex-shrink-0 px-1.5 py-0.5 rounded border text-[10px] font-medium ${MARK_STYLES[brand.mark_type]}`}
            >
              {MARK_LABELS[brand.mark_type]}{brand.is_promoted ? ' · partner' : ''}
            </span>
          )}
          {brand.is_own && (
            <span className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[10px] font-medium whitespace-nowrap">
              own mark
            </span>
          )}
          {!brand.is_own && brand.excluded_reason && (
            <span className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 text-[10px] font-medium whitespace-nowrap">
              {REASONS.find((r) => r.value === brand.excluded_reason)?.label ?? brand.excluded_reason}
            </span>
          )}
        </div>

        {/* Raw reads, so a bad snap stays visible rather than being hidden by
            the resolver's tidy-up. This is often the tell. */}
        {brand.raw_names?.some((r) => r && r !== brand.brand_name) && (
          <p className="text-[11px] text-gray-400 mt-0.5 truncate"
             title={brand.raw_names.filter(Boolean).join(', ')}>
            read as: {brand.raw_names.filter(Boolean).join(', ')}
          </p>
        )}

        {/* Peak size is gone: it is a diagnostic about one frame, not a
            property of the brand's delivery, and the drawer still shows it
            per appearance where it can be checked against the image. The
            three that remain read left to right as the argument — how much
            screen, for how long, worth how much. */}
        <div className="grid grid-cols-3 gap-1.5 mt-2 text-[11px]">
          <div>
            <p className="text-gray-400">Area-sec</p>
            <p className="font-semibold text-gray-900 tabular-nums">
              {Number(brand.total_area_seconds).toFixed(2)}
            </p>
          </div>
          <div>
            <p className="text-gray-400">On screen</p>
            <p className="font-semibold text-gray-900 tabular-nums">{secs(brand.total_duration_ms)}</p>
          </div>
          {/* A DASH, NEVER $0.00. Zero is a measurement; "nobody has told us
              the audience yet" is not, and a sponsor shown $0.00 against real
              exposure would be right to complain. */}
          <div>
            <p className="text-gray-400">Value</p>
            <p className="font-semibold text-emerald-700 tabular-nums">
              {formatMoney(valueOf(Number(brand.total_area_seconds), rate).value)}
            </p>
          </div>
        </div>

        <p className="text-[11px] text-gray-500 mt-2 truncate" title={brand.zones?.split(',').join(', ')}>
          {brand.zones?.split(',').join(', ')}
        </p>

        {/* A sponsored brand has NO exclusion control, by rule rather than
            by disabling one: this is inventory somebody paid for, and the
            sponsor link is derived from the partner list on every read, so
            there is nothing here a reviewer could legitimately overrule. The
            way to stop it counting is to soft-delete the partner, which is a
            commercial act performed where partners live - not a
            per-broadcast checkbox.

            Checked BEFORE is_own: an active partner that also matches a club
            mark is still inventory.

            It shows the PARTNER only when that name differs from the
            detected one, because "Chase" resolving to "Chase Bank" is not
            knowable from the card otherwise. When they match there is
            nothing to add, and the footer - divider included - is dropped
            rather than padded with the word "Sponsored", which under a tab
            already headed Sponsored says nothing at all. */}
        {footer && (
          <div className="mt-2.5 pt-2.5 border-t border-gray-100">{footer}</div>
        )}
      </div>
    </div>
  )
}

export default BroadcastBrandCard
